import { Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import ImageUpload from '@/components/ImageUpload'
import type { EventService } from '@/types'

type Props = {
  service?: EventService & { status?: string }
  serviceTypes: string[]
  priceRanges: string[]
  statuses: string[]
  additionalServiceOptions: string[]
}

export default function EventServiceForm({ service, serviceTypes, priceRanges, statuses, additionalServiceOptions }: Props) {
  const isEdit = !!service

  const { data, setData, post, patch, processing, errors } = useForm({
    event_service: {
      name: service?.name || '',
      service_type: service?.service_type || '',
      description: service?.description || '',
      price_range: service?.price_range || '',
      status: service?.status || 'draft',
      featured: service?.featured || false,
      contact_email: service?.contact_email || '',
      phone: service?.phone || '',
      website: service?.website || '',
      street_address: service?.street_address || '',
      city: service?.city || '',
      state: service?.state || '',
      zip: service?.zip || '',
      hide_address: service?.hide_address || false,
      instagram_url: service?.instagram_url || '',
      twitter_url: service?.twitter_url || '',
      facebook_url: service?.facebook_url || '',
      linkedin_url: service?.linkedin_url || '',
      youtube_url: service?.youtube_url || '',
      tiktok_url: service?.tiktok_url || '',
      years_experience: service?.years_experience ?? '',
      portfolio_url: service?.portfolio_url || '',
      video_url: service?.video_url || '',
      additional_services: service?.additional_services || [],
      capacity: service?.capacity ?? '',
      seated_capacity: service?.seated_capacity ?? '',
      standing_capacity: service?.standing_capacity ?? '',
      square_footage: service?.square_footage ?? '',
      floors: service?.floors ?? '',
      floor_plan_url: service?.floor_plan_url || '',
      private_rooms: service?.private_rooms || '',
    },
    cover_image: '' as string,
    logo: '' as string,
    remove_cover_image: '0',
    remove_logo: '0',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isEdit) {
      patch(`/admin/event_services/${service!.id}`)
    } else {
      post('/admin/event_services')
    }
  }

  function updateField(field: string, value: string | boolean | number | string[]) {
    setData('event_service', { ...data.event_service, [field]: value })
  }

  function toggleAdditionalService(svc: string) {
    const current = data.event_service.additional_services as string[]
    const updated = current.includes(svc)
      ? current.filter(s => s !== svc)
      : [...current, svc]
    updateField('additional_services', updated)
  }

  const isVenue = data.event_service.service_type === 'Venue'

  return (
    <div className="max-w-2xl">
      <Link
        href="/admin/event_services"
        className="mb-4 inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to Event Services
      </Link>

      <h1 className="text-3xl font-semibold tracking-tight font-serif">
        {isEdit ? 'Edit Event Service' : 'New Event Service'}
      </h1>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Business Name *</Label>
              <Input
                id="name"
                value={data.event_service.name}
                onChange={e => updateField('name', e.target.value)}
                className="mt-1"
              />
              {errors['event_service.name'] && <p className="mt-1 text-base text-destructive">{errors['event_service.name']}</p>}
            </div>

            <div>
              <Label htmlFor="service_type">Service Type *</Label>
              <Select
                value={data.event_service.service_type}
                onValueChange={v => updateField('service_type', v)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {[...serviceTypes].sort().map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={data.event_service.description}
                onChange={e => updateField('description', e.target.value)}
                className="mt-1"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price_range">Price Range</Label>
                <Select
                  value={data.event_service.price_range}
                  onValueChange={v => updateField('price_range', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a price range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {priceRanges.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={data.event_service.status}
                  onValueChange={v => updateField('status', v)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {statuses.map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="years_experience">Years of Experience</Label>
              <Input
                id="years_experience"
                type="number"
                value={data.event_service.years_experience}
                onChange={e => updateField('years_experience', e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="featured"
                checked={data.event_service.featured as boolean}
                onCheckedChange={v => updateField('featured', v)}
              />
              <Label htmlFor="featured">Featured service</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="contact_email">Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={data.event_service.contact_email}
                onChange={e => updateField('contact_email', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={data.event_service.phone}
                onChange={e => updateField('phone', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={data.event_service.website}
                onChange={e => updateField('website', e.target.value)}
                className="mt-1"
                placeholder="https://"
              />
            </div>
            <div>
              <Label htmlFor="portfolio_url">Portfolio URL</Label>
              <Input
                id="portfolio_url"
                value={data.event_service.portfolio_url}
                onChange={e => updateField('portfolio_url', e.target.value)}
                className="mt-1"
                placeholder="https://"
              />
            </div>
            <div>
              <Label htmlFor="video_url">Video URL</Label>
              <Input
                id="video_url"
                value={data.event_service.video_url}
                onChange={e => updateField('video_url', e.target.value)}
                className="mt-1"
                placeholder="https://youtube.com/..."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="street_address">Street Address</Label>
              <Input
                id="street_address"
                value={data.event_service.street_address}
                onChange={e => updateField('street_address', e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={data.event_service.city}
                  onChange={e => updateField('city', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={data.event_service.state}
                  onChange={e => updateField('state', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="zip">Zip Code</Label>
                <Input
                  id="zip"
                  value={data.event_service.zip}
                  onChange={e => updateField('zip', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="hide_address"
                checked={data.event_service.hide_address}
                onCheckedChange={v => updateField('hide_address', v)}
              />
              <Label htmlFor="hide_address">Hide address from public profile</Label>
            </div>
          </CardContent>
        </Card>

        {isVenue && (
          <Card>
            <CardHeader>
              <CardTitle>Venue Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="capacity">Total Capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={data.event_service.capacity}
                    onChange={e => updateField('capacity', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="seated_capacity">Seated</Label>
                  <Input
                    id="seated_capacity"
                    type="number"
                    value={data.event_service.seated_capacity}
                    onChange={e => updateField('seated_capacity', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="standing_capacity">Standing</Label>
                  <Input
                    id="standing_capacity"
                    type="number"
                    value={data.event_service.standing_capacity}
                    onChange={e => updateField('standing_capacity', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="square_footage">Square Footage</Label>
                  <Input
                    id="square_footage"
                    type="number"
                    value={data.event_service.square_footage}
                    onChange={e => updateField('square_footage', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="floors">Floors</Label>
                  <Input
                    id="floors"
                    type="number"
                    value={data.event_service.floors}
                    onChange={e => updateField('floors', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="floor_plan_url">Floor Plan URL</Label>
                <Input
                  id="floor_plan_url"
                  value={data.event_service.floor_plan_url}
                  onChange={e => updateField('floor_plan_url', e.target.value)}
                  className="mt-1"
                  placeholder="https://"
                />
              </div>
              <div>
                <Label htmlFor="private_rooms">Private Rooms / Waiting Areas</Label>
                <Input
                  id="private_rooms"
                  value={data.event_service.private_rooms}
                  onChange={e => updateField('private_rooms', e.target.value)}
                  className="mt-1"
                  placeholder="e.g. 2 VIP green rooms"
                />
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Additional Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {additionalServiceOptions.map(svc => (
                <label key={svc} className="flex items-center gap-2 text-base">
                  <Checkbox
                    checked={(data.event_service.additional_services as string[]).includes(svc)}
                    onCheckedChange={() => toggleAdditionalService(svc)}
                  />
                  {svc}
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Media</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Cover Image</Label>
              <ImageUpload
                currentUrl={service?.cover_image_url}
                onUpload={signedId => {
                  setData('cover_image', signedId)
                  setData('remove_cover_image', signedId ? '0' : '1')
                }}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Business Logo</Label>
              <ImageUpload
                currentUrl={service?.logo_url}
                onUpload={signedId => {
                  setData('logo', signedId)
                  setData('remove_logo', signedId ? '0' : '1')
                }}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Social Media</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'instagram_url', label: 'Instagram' },
              { key: 'twitter_url', label: 'X / Twitter' },
              { key: 'facebook_url', label: 'Facebook' },
              { key: 'linkedin_url', label: 'LinkedIn' },
              { key: 'youtube_url', label: 'YouTube' },
              { key: 'tiktok_url', label: 'TikTok' },
            ].map(({ key, label }) => (
              <div key={key}>
                <Label htmlFor={key}>{label}</Label>
                <Input
                  id={key}
                  value={(data.event_service as Record<string, string | boolean | number | string[]>)[key] as string}
                  onChange={e => updateField(key, e.target.value)}
                  className="mt-1"
                  placeholder="https://"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={processing}>
            {isEdit ? 'Update Service' : 'Create Service'}
          </Button>
          <Link href="/admin/event_services">
            <Button type="button" variant="outline">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  )
}
